import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from 'react-i18next';
import GamificationWidget from './GamificationWidget';
import ACommonDataField from "../../../components/GenericComponents/CommonDataField";
import ButtonsPanel from "./ButtonsPanel";
import useAlertService from "../../../hooks/useAlertService";
import { prepareUpdatePlayerGeneralDto } from "./model/playerEditModel";
import { updatePlayerGeneral } from "../../../store/players/general/actions";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";
import { SITE_PERMISSION } from "../../../common/constants/common"
import { SITE_BRAND, SMARTICO_DRIVE_LINKS } from "../../../constants/siteBrands"
import userSearch from "../../../assets/images/userSearch.svg";
import Tooltip from "@mui/material/Tooltip";
import doc from "../../../assets/images/common/doc.svg";
import RenderVIPAudit from "./RenderVIPAudit";
import { getAgentsList } from "../../../store/agents/agents/actions";
import { getPlayerVipAuditList } from "../../../store/players/audit/actions";
import './vipStyles.scss';
import useVipSecretLevel from "../../../hooks/useVipSecretLevel";
import { A_COMMON_DATA_FIELD } from "../../../constants/aCommonDataField"
import CustomModal from "../../../common/components/CustomModal"
import { getAgentsManagement } from "../../../helpers/agentApi"

const VipTabContent = (props) => {
    const { playerId, playerModel, setPlayerModel, playerOid } = props;
    const { vipAuditList } = useSelector(state => state.PlayerAudit);
    const { isLoading, playerGeneral: initialState, successMessage } = useSelector(state => state.PlayerGeneral);
    const { active: activeBrand } = useSelector(state => state.Brand);
    const { agentSelf } = useSelector(state => state.AgentSelf);
    const { agentsList } = useSelector(state => state.AgentList);

    const [comment, setComment] = useState('');
    const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const alertService = useAlertService();

    const agentPermissions = agentSelf?.permissions;
    const canEditAgentOwner = agentPermissions.includes(SITE_PERMISSION.Player__Change_Agent_Owner);
    const canActivateSLA = agentPermissions.includes(SITE_PERMISSION.Player__Activate_SLA);

    const {
        fetchSecretVipLevelAvailable,
        activateSecretVipLevelForPlayer,
        isLoadingAvailableSecretLevel,
        isSecretLevelActivated,
        addVipCommentForPlayer,
        vipComment
    } = useVipSecretLevel();

    useEffect(() => {
        dispatch(getAgentsList());
        dispatch(getPlayerVipAuditList(playerOid));
        fetchSecretVipLevelAvailable(playerOid);
    }, []);

    useEffect(() => {
        if (vipComment.length) {
            setComment(vipComment);
        }
    }, [vipComment])

    const [managementList, setManagementList] = useState(null);

    useEffect(() => {
        const fetchManagementList = async () => {
            try {
                const managementListReq = await getAgentsManagement();
                setManagementList(managementListReq?.data?.result || []);
            } catch (error) {
                console.log('Error in fetchManagementList:', error);
            }
        };
        fetchManagementList();
    }, [activeBrand?.siteName]);

    const brandsAgentList = useMemo(() => {
        if (!managementList || !agentsList) return [];

        return agentsList.filter(agent => {
            const managementAgent = managementList.find(management =>
              management.id === agent.id &&
              management.status === 'Active' &&
              management.brands.includes(activeBrand?.siteName)
            );

            return !!managementAgent;
        });
    }, [managementList, agentsList, activeBrand?.siteName]);

    const fields = [
        {
            component: 'select-vip-level',
            stateKey: 'vipLevel',
            hasClear: false,
            defaultValue: playerModel.vipLevel,
            disabled: true,
        },
        {
            component: 'select-agent-owner',
            stateKey: 'agentOwnerId',
            hasClear: true,
            options: brandsAgentList || [],
            defaultValue: playerModel.agentOwnerId === null ? '' : playerModel.agentOwnerId,
            disabled: !canEditAgentOwner
        },
    ];

    const onClickSecretLevelActivation = () => {
        setIsShowConfirmModal(true);
    }

    const isEditVipComment = useMemo(() => {
        return !isEqual(vipComment, comment)
    }, [vipComment, comment]);

    const isEditGeneral = useMemo(() => {
        return !isEqual(initialState.vipLevel, playerModel.vipLevel) ||
          !isEqual(initialState.agentOwnerId, playerModel.agentOwnerId)
    }, [initialState.vipLevel, playerModel.vipLevel, initialState.agentOwnerId, playerModel.agentOwnerId]);

    const handleSave = () => {
        const dto = prepareUpdatePlayerGeneralDto(
          initialState,
          playerModel
        );
        if (dto.agentOwnerId === '') {
            dto.agentOwnerId = null;
        }

        if (isEditGeneral) {
            dispatch(updatePlayerGeneral({ id: playerModel.oid, dto }));
        }

        if (isEditVipComment) {
            addVipCommentForPlayer(playerModel.oid, comment);
        }
    };

    const handleCancel = () => {
        setPlayerModel(initialState);
        setComment(vipComment);
    }

    const handleChange = (stateKey) => (value) => {
        if (stateKey === 'agentOwnerId' && value === '') {
            value = null;
        }
        if (stateKey === 'vipComment') {
            setComment(value);
        }
        setPlayerModel(prevState => ({
            ...prevState,
            [stateKey]: value
        }));
    }

    useEffect(() => {
        if (successMessage) {
            alertService.showSuccess(t(successMessage));
        }
    }, [successMessage]);


    const openSmarticoLink = (link) => {
        window.open(link, '_blank');
    }

    const activateSecretLevel = () => {
        activateSecretVipLevelForPlayer(playerOid);
        setIsShowConfirmModal(false);
    }

    return (
      <div className="vip-tab-container">
          <div className="vip-tab-left">
              {fields.map((item, index) => (
                <div key={index} className="vip-level-field">
                    <ACommonDataField
                      component={item.component}
                      label={t(`crm.${item.label || item.stateKey}`)}
                      value={item.defaultValue}
                      options={item.options}
                      handleChange={handleChange(item.stateKey)}
                      hasClear={item.hasClear}
                      disabled={item.disabled}
                    />
                </div>
              ))}
              <div className="vip-tab-smartico">
                  <GamificationWidget userId={playerId} />
                  {SMARTICO_DRIVE_LINKS[activeBrand?.siteName] && (
                    <Tooltip title={t("crm.openSmarticoDrive")} arrow>
                        <button
                          className="btn vip-btn"
                          onClick={() => openSmarticoLink(SMARTICO_DRIVE_LINKS[activeBrand?.siteName].replace("{{PLAYER_ID}}", playerId))}
                        >
                            <img src={userSearch} alt="Smartico users" />
                        </button>
                    </Tooltip>
                  )}
              </div>
              {activeBrand?.siteName === SITE_BRAND.WINPOTMX && (
                <Tooltip
                  title={t("crm.secretLevelTooltip")}
                  placement="top"
                  arrow
                >
                    <button
                      className="btn btn-rounded btn-primary vip-secret-activation"
                      disabled={isSecretLevelActivated || isLoadingAvailableSecretLevel || !canActivateSLA}
                      onClick={onClickSecretLevelActivation}
                    >
                        {t("crm.activateVipSecretLevel")}
                    </button>
                </Tooltip>
              )}
              <div className="vip-comment-field">
                  <ACommonDataField
                    multiline
                    fulWidth={true}
                    component={A_COMMON_DATA_FIELD.TEXT}
                    label={"Comment"}
                    value={comment}
                    handleChange={handleChange("vipComment")}
                  />
              </div>
          </div>

          <div className="vertical-separator" />

          <div className="vip-tab-right">
              {vipAuditList && vipAuditList.length > 0 && (
                <div className="vip-audit">
                    <div className="vip-audit-header">
                        <img className="me-3" src={doc} alt="" />
                        <span>{t("crm.auditOfChanges")}</span>
                    </div>
                    <RenderVIPAudit vipAuditList={vipAuditList} />
                </div>
              )}
          </div>

          {(isEditVipComment || isEditGeneral) && (
            <ButtonsPanel
              onSave={handleSave}
              onCancel={handleCancel}
              isDisabled={isLoading}
            />
          )}

          {isShowConfirmModal &&
            <CustomModal
              titleText={t('crm.confirmationHeader')}
              isOpen={isShowConfirmModal}
              onToggle={() => setIsShowConfirmModal(!isShowConfirmModal)}
              onClick={activateSecretLevel}
              onClickCancelButton={() => setIsShowConfirmModal(false)}
              btnText={'save'}
              cancelBtnText={'cancel'}
              withCancelButton={true}
              bodyRender={() => (
                <div
                  style={{
                      textAlign: 'center',
                      width: '100%',
                  }}
                >
                    {t('crm.modalConfirmSLA')}
                </div>
              )}
            />
          }
      </div>

    );
}

export default React.memo(VipTabContent);